import { supabase } from "../config/supabase";

/**
 * Service for managing member attendance at CIPA meetings
 * Integrates with cp_membros_presenca table (NR-05 compliance)
 */

export type StatusPresenca = "presente" | "ausente" | "justificado";

export interface MembroPresenca {
  id?: string;
  ata_id: string;
  membro_id?: string | null;
  nome_membro: string;
  cargo: string;
  tipo_membro: string;
  status: StatusPresenca;
  justificativa?: string;
  hora_chegada?: string;
  hora_saida?: string;
  created_at?: string;
  updated_at?: string;
}

export interface MembroCIPA {
  id: string;
  nome: string;
  cargo: string;
  tipo: string; // efetivo, suplente
  representacao: string; // empregador, empregados
  ativo: boolean;
}

/**
 * Service for managing attendance records
 */
export const presencaService = {
  /**
   * List all active CIPA members for a company
   */
  async listarMembrosCIPA(empresaId: string): Promise<MembroCIPA[]> {
    try {
      const { data, error } = await supabase
        .from("cp_cipa_membros")
        .select(
          `
          id,
          nome,
          cargo,
          tipo,
          representacao,
          ativo
        `,
        )
        .eq("empresa_id", empresaId)
        .eq("ativo", true)
        .order("nome");

      if (error) {
        console.error("❌ Erro ao listar membros da CIPA:", error);
        throw error;
      }

      return (data || []) as MembroCIPA[];
    } catch (error) {
      console.error("❌ Erro ao listar membros da CIPA:", error);
      throw error;
    }
  },

  /**
   * Create attendance records for a meeting
   */
  async registrarPresencas(
    presencas: Omit<MembroPresenca, "id" | "created_at" | "updated_at">[],
  ): Promise<MembroPresenca[]> {
    try {
      const { data, error } = await supabase
        .from("cp_membros_presenca")
        .insert(presencas)
        .select();

      if (error) {
        console.error("❌ Erro ao registrar presenças:", error);
        throw error;
      }

      console.log("✅ Presenças registradas com sucesso:", data);
      return data as MembroPresenca[];
    } catch (error) {
      console.error("❌ Erro ao registrar presenças:", error);
      throw error;
    }
  },

  /**
   * Update attendance record
   */
  async atualizarPresenca(
    id: string,
    updates: Partial<MembroPresenca>,
  ): Promise<MembroPresenca> {
    try {
      const { data, error } = await supabase
        .from("cp_membros_presenca")
        .update(updates)
        .eq("id", id)
        .select()
        .single();

      if (error) {
        console.error("❌ Erro ao atualizar presença:", error);
        throw error;
      }

      console.log("✅ Presença atualizada com sucesso:", data);
      return data as MembroPresenca;
    } catch (error) {
      console.error("❌ Erro ao atualizar presença:", error);
      throw error;
    }
  },

  /**
   * Get attendance records for a specific meeting
   */
  async obterPresencasPorAta(ataId: string): Promise<MembroPresenca[]> {
    try {
      const { data, error } = await supabase
        .from("cp_membros_presenca")
        .select("*")
        .eq("ata_id", ataId)
        .order("nome_membro");

      if (error) {
        console.error("❌ Erro ao obter presenças:", error);
        throw error;
      }

      return (data || []) as MembroPresenca[];
    } catch (error) {
      console.error("❌ Erro ao obter presenças:", error);
      throw error;
    }
  },

  /**
   * Delete attendance record
   */
  async excluirPresenca(id: string): Promise<void> {
    try {
      const { error } = await supabase
        .from("cp_membros_presenca")
        .delete()
        .eq("id", id);

      if (error) {
        console.error("❌ Erro ao excluir presença:", error);
        throw error;
      }

      console.log("✅ Presença excluída com sucesso");
    } catch (error) {
      console.error("❌ Erro ao excluir presença:", error);
      throw error;
    }
  },

  /**
   * Calculate quorum for a meeting
   * Returns percentage and whether it meets minimum requirement (50% + 1)
   */
  calcularQuorum(presencas: MembroPresenca[]): {
    total: number;
    presentes: number;
    ausentes: number;
    justificados: number;
    percentualPresentes: number;
    quorumAtingido: boolean;
  } {
    const total = presencas.length;
    const presentes = presencas.filter((p) => p.status === "presente").length;
    const ausentes = presencas.filter((p) => p.status === "ausente").length;
    const justificados = presencas.filter(
      (p) => p.status === "justificado",
    ).length;

    const percentualPresentes = total > 0 ? (presentes / total) * 100 : 0;

    // Quórum mínimo: 50% + 1 (maioria absoluta)
    const quorumMinimo = Math.floor(total / 2) + 1;
    const quorumAtingido = presentes >= quorumMinimo;

    return {
      total,
      presentes,
      ausentes,
      justificados,
      percentualPresentes,
      quorumAtingido,
    };
  },

  /**
   * Generate attendance list from CIPA members for a new meeting
   * Pre-fills with all active members marked as "presente" by default
   */
  async gerarListaPresencaInicial(
    empresaId: string,
    ataId: string,
  ): Promise<Omit<MembroPresenca, "id" | "created_at" | "updated_at">[]> {
    try {
      const membros = await this.listarMembrosCIPA(empresaId);

      const presencas = membros.map((membro) => ({
        ata_id: ataId,
        membro_id: membro.id,
        nome_membro: membro.nome,
        cargo: membro.cargo,
        tipo_membro: membro.tipo,
        status: "presente" as StatusPresenca,
        justificativa: undefined,
      }));

      return presencas;
    } catch (error) {
      console.error("❌ Erro ao gerar lista de presença inicial:", error);
      throw error;
    }
  },
};
